import React, {
  CSSProperties,
  MutableRefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useSupportsHover } from 'hooks/useSupportsHover';
import { acceptSeatOffer } from 'reducers/waitlist';
import type { WaitlistEntry } from 'types/waitlist';

import { cardAltText, cardFaceSrc } from 'components/Card/deck';
import { subscribeGridVisibility } from 'components/MultiTableGrid/gridScrollChannel';
import {
  getDragState,
  isSpringingBack,
  subscribeDragOffset
} from 'components/RebindingTableView/tableSwipeDrag';
import { PENDING_SHUTDOWN_LABEL } from 'components/SaloonLobby/shutdownCopy';
import { ActiveTable, Rank, Suit } from 'components/SaloonLobby/types';
import useWaitlistCountdown from 'components/Waitlist/useWaitlistCountdown';

import {
  DotPlacement,
  dotPlacement,
  forgetDotPlacement,
  lastDotPlacement,
  rememberDotPlacement,
  samePlacement
} from './selectionDot';
import { TurnRingColors, turnRingColors, turnSecondsLeft } from './turnRingColor';
import { useDockOutline } from './useDockOutline';
import { useHandOutcome } from './useHandOutcome';

import styles from './YourTables.module.scss';

interface YourTablesProps {
  tables: ActiveTable[];
  onOpenTable?: (gameID: string, game: string) => boolean | void;
  // `bare` drops the lobby chrome — the "Your Tables" label and the strip's
  // gradient background/border — and renders ONLY the card fans. The in-table
  // header overlays the fans directly on the felt where the design placed them,
  // so it opts out of the chrome while still sharing the exact same cards.
  bare?: boolean;
  // gameID of the table currently being viewed — the selection dot slides to
  // sit under its fan.
  activeGameID?: string;
  // When provided, a trailing dashed "+" card is appended to the fan row so the
  // player can add another table directly from the table view (parity with the
  // Unity reference). Omitted in the lobby strip, which already lists every
  // table.
  onAddTable?: () => void;
  // Draw the player's queued waitlist spots as cards (the default). The
  // switcher's lone "+" (EN-10780) turns this off: it is an add-only control,
  // and a queued table surfacing there would put an always-visible card beside
  // the "+" that the collapsed badge never shows.
  showWaitlist?: boolean;
  // Plays the staggered "fan-in" entrance on the bare previews (used by the
  // switcher dropdown so the cards appear in sequence on open). Off for the
  // always-visible landscape fan.
  animateIn?: boolean;
  // Bare fans normally re-center themselves to the page (so a lone fan row sits
  // mid-screen). When the fans live inside a flex group with a sibling (e.g. the
  // landscape grid button), set false so they DON'T self-shift — the parent
  // centers the whole group instead, keeping the button tucked beside the fans.
  autoCenter?: boolean;
  // GRID view: one visibility dot under each tile whose table is on the grid's
  // screen (orange = active), driven by the grid's scroll channel. Also
  // suppresses the single selection dot — the two answer the same question in
  // different modes and must never show together.
  gridViewDots?: boolean;
  // Ref attached to the sticky lobby strip's root so the lobby can measure its
  // pinned height and pin the tournament day-filter flush beneath it. Only the
  // (non-bare) lobby strip forwards it; a callback ref also fires with null when
  // the strip unmounts (no active tables), letting the caller reset the offset.
  rootRef?: React.Ref<HTMLDivElement>;
  // A class for the lobby panel's root (the same element `rootRef` reaches), so
  // the lobby's stylesheet can say "what follows the panel" with a class it
  // owns instead of matching this module's generated ident (review,
  // 2026-09-16). Ignored by the bare fans, which have no panel.
  className?: string;
  // Mobile multi-table NAVIGATION STRIP layout (bare only). The row is a
  // fixed-capacity strip centred in its band: the "+" leads, the table cards
  // follow in the ONE shared subscription order: a new table takes the slot a
  // closed table left, or the LEFTMOST slot when none is free (EN-10545,
  // EN-10857). This component renders whatever order it is handed — the
  // order itself is decided in useActiveTables and the grid sorts its cells
  // by the same record, so strip, dock and grid can never disagree. Slot size
  // and pitch are FIXED (see the strip geometry section), so switching, opening
  // or closing a table never resizes or reflows the cards. Off (the default)
  // keeps the centered fan row the lobby / desktop overlay / switcher render.
  stripLayout?: boolean;
  // The table cap (MAX_OPEN_TABLES) — the strip sizes its slots for a FULL
  // strip so its geometry doesn't depend on how many tables are open now.
  stripCapacity?: number;
  /**
   * Rendered after the last tile, INSIDE the row, at exactly TILE_GAP from that
   * tile's drawn edge.
   *
   * A parent cannot place this itself: each tile is centred in a HAND_BOX-wide
   * slot, so the row's box ends with leftover slot padding whose size depends on
   * the last hand's card count. A sibling placed outside would sit that padding
   * further out than the "+" sits on the other end, which is exactly the
   * lopsided look this fixes (Jason 2026-08-17). The row owns its own rhythm.
   */
  trailing?: React.ReactNode;
}

// Lay N cards out as an arched fan, the way a player holds a hand. Each card
// gets an angle, evenly spread from -max to +max; the arch shape itself comes
// from the .card transform-origin (a pivot below the cards) in the SCSS, so
// rotating each card swings it around that low point — tops fan apart, bottoms
// stay together. Omaha (4) uses a wider arch than Hold'em (2). `spread` > 1
// widens the whole fan (used on hover).
const cardAngles = (count: number, spread = 1, omahaMax = 22): number[] => {
  if (count <= 1) return [0];
  const max = (count >= 4 ? omahaMax : 8) * spread;
  const step = (max * 2) / (count - 1);
  return Array.from({ length: count }, (_, i) => -max + i * step);
};

interface PlayingCardProps {
  rank: Rank;
  suit: Suit;
  angle: number;
  // Higher z stacks on top — caller passes the array index so cards layer
  // left-behind-right consistently regardless of DOM order.
  z: number;
  // Narrower than the CSS default, for hands that have to fit more cards in the
  // same slot (Omaha in the navigation strip). Kept as a width override rather
  // than a scale so the cards stay the same HEIGHT and the fan's baseline — and
  // everything aligned to it — doesn't move.
  width?: number;
}

// Centring is `left: 50%` plus a negative half-width margin, so a width
// override has to bring its own margin with it. Height is deliberately left
// alone (the fan's baseline must not move); the artwork keeps its own shape
// inside the box via object-fit (see .face in the SCSS).
const cardWidthStyle = (width?: number): CSSProperties =>
  width === undefined ? {} : { width, marginLeft: -width / 2 };

// The same deck artwork as the felt (Card/deck.ts) — the lobby hand IS the
// table's card, only smaller. Nothing is typeset here.
const PlayingCard: React.FC<PlayingCardProps> = ({ rank, suit, angle, z, width }) => (
  <div
    className={styles.card}
    style={{ transform: `rotate(${angle}deg)`, zIndex: z, ...cardWidthStyle(width) }}>
    <img
      className={styles.face}
      src={cardFaceSrc(rank, suit)}
      alt={cardAltText(rank, suit)}
      draggable={false}
    />
  </div>
);

// --- Waitlist entries -------------------------------------------------------
// Tables the player is queued for (but not seated at) ride in the strip
// alongside their active hands as face-down card backs with a status stamp.

// Six, not the felt's 22: same idea, sized to an 86px tile.
const ORB_KEYS = [0, 1, 2, 3, 4, 5];
const PUFF_KEYS = ['p1', 'p2', 'p3', 'p4', 'p5', 'p6', 'p7', 'p8'];
const DEBRIS_KEYS = ['d1', 'd2', 'd3', 'd4', 'd5', 'd6', 'd7', 'd8', 'd9', 'd10', 'd11', 'd12'];

// Matches the .handJoinReady.poofing vanish (0.85s) + the 1.2s debris flight.
const POOF_MS = 1200;

// Omaha/Hi-Lo are 4-hole-card games, Hold'em is 2 — the waitlist hand fans
// the matching number of face-down cards (WL-003, PO-approved).
const cardBackCount = (gameType: WaitlistEntry['gameType']): number =>
  gameType === 'omaha' ? 4 : 2;

// Width of a fan's framing preview box. Bare (grid-header / overlay) previews use
// a slightly wider box for padding around the cards; the lobby strip hides the
// frame (`.previewFrame { display: none }`) so the width is moot there.
const previewBoxWidth = (count: number, bare: boolean): number => {
  // BARE rows (the strip, the grid header, the switcher) draw every tile at the
  // WIDEST span regardless of card count. The row's pitch is already pinned to
  // the widest fan so card-count changes never reflow it — but with the pitch
  // fixed and the tile width varying, the VISIBLE gap varied instead: two
  // Hold'em tiles sat ~9px further apart than the "+" sat from its neighbour,
  // and Omaha tiles closer than Hold'em ones (Jason 2026-08-17, at 500px).
  // Uniform tiles make every gap the single designed gap at every width, and
  // keep the no-reflow invariant for free. The lobby strip (non-bare) still
  // hugs its cards — its row is content-sized, so its gaps were already right.
  if (bare) return 65 - 3 + 12;
  return (count >= 4 ? 65 : 56) - 3;
};

interface WaitlistHandProps {
  entry: WaitlistEntry;
  // Tapping a waitlist entry opens the table — for a "Join" (seat-ready) entry
  // the table view auto-triggers the seat-offer buy-in (WL-004).
  onOpenTable?: (gameID: string, game: string) => boolean | void;
  // Per-hand layout override (the padding-aware overlap margin in the bare
  // in-table fan row); omitted in the lobby strip which uses a plain gap.
  style?: CSSProperties;
  // Bare (grid-header / overlay) previews frame each fan in a box like the
  // seated/observed hands; the lobby strip leaves it off (CSS-hidden anyway).
  bare?: boolean;
  // True when this is the table the player is currently VIEWING. A queued table
  // deliberately keeps its waitlist card instead of flipping to an observing
  // hand, so these cards have to be able to carry the
  // selection marker too — otherwise opening a table you are only queued on
  // left the whole row with no aria-current, and SelectionDot (which homes in on
  // [aria-current="true"]) had nothing to anchor to and hid itself.
  isActive?: boolean;
  // GRID view: render this tile's visibility dot (see GridVisibilityDot). The
  // grid suppresses the sliding SelectionDot in favour of one dot per tile, so a
  // waitlist card that has a grid cell needs its own — without it the row's
  // ONLY unmarked tile is the queued one, and a queued table that is the focused
  // cell shows no indicator at all.
  gridDot?: { active: boolean };
  // The open table this card sits in is shutting down and the player isn't
  // seated: not navigable, the same guard the table's own Hand applies.
  disabled?: boolean;
  // Rendered once an expired JOIN card has played its poof, so a table that is
  // still OPEN keeps a navigable tile in its slot instead of a hole.
  whenGone?: React.ReactNode;
}

const WaitingHand: React.FC<WaitlistHandProps> = ({
  entry,
  onOpenTable,
  style,
  bare = false,
  isActive = false,
  gridDot,
  disabled = false
}) => {
  const count = cardBackCount(entry.gameType);
  return (
    <button
      type='button'
      style={style}
      className={`${styles.hand} ${styles.handWaitlist} ${count === 4 ? styles.fan4 : ''}`}
      onClick={() => (disabled ? undefined : onOpenTable?.(String(entry.gameId), entry.gameType))}
      disabled={disabled}
      title={`On waitlist · position ${entry.position}`}
      // Same single source of truth as the seated hands: the dot follows
      // aria-current, so a queued table being viewed keeps the indicator.
      aria-current={isActive ? 'true' : undefined}
      aria-label={`On waitlist, position ${entry.position}`}>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className={styles.cardBack} />
      ))}
      <span className={styles.waitStamp}>WAIT - {entry.position}</span>
      {/* Framing box LAST (bare only — CSS-hidden in the lobby) so it doesn't
          shift the cardBacks' :nth-child fan rules; the cards sit above it via
          their z-index. */}
      <span
        className={styles.previewFrame}
        style={{ width: previewBoxWidth(count, bare) }}
        aria-hidden
      />
      {gridDot && <GridVisibilityDot gameID={String(entry.gameId)} active={gridDot.active} />}
    </button>
  );
};

// "Your seat is ready" — cards shake + pulse green, a countdown ticks over
// them, and when the accept window expires the entry detonates in a poof.
const JoinReadyHand: React.FC<WaitlistHandProps> = ({
  entry,
  onOpenTable,
  style,
  bare = false,
  isActive = false,
  gridDot,
  disabled = false,
  whenGone = null
}) => {
  const dispatch = useAppDispatch();
  const { secondsRemaining, expired } = useWaitlistCountdown({
    waitForAcceptStartTime: entry.waitForAcceptStartTime,
    waitlistTimeOut: entry.waitlistTimeOut,
    bumpCount: entry.bumpCount,
    debugEnabled: false
  });
  const [phase, setPhase] = useState<'live' | 'poofing' | 'gone'>('live');
  const count = cardBackCount(entry.gameType);

  // Claim the seat — this replaces the retired "Your Seat is Ready" modal's
  // Accept. Flagging the offered game (acceptSeatOffer) makes its own table view
  // open the buy-in flow once we navigate there, exactly as the modal did. Not
  // when the open reports it was rejected: the flag would outlive it and open
  // the buy-in whenever that table was next opened.
  // Only a live offer can be claimed: from the moment it expires — the poof
  // included — the seat is no longer the player's to take. A re-offer revives
  // the card (expired -> false, phase back to 'live'). With no way to open the
  // table there is no claim either: the flag must follow a real open.
  const claimable = !disabled && !expired && phase === 'live';
  const claimSeat = () => {
    if (!claimable || !onOpenTable) return;
    if (onOpenTable(String(entry.gameId), entry.gameType) === false) return;
    dispatch(acceptSeatOffer(entry.gameId));
  };

  useEffect(() => {
    // A live (or re-offered) seat must always render the countdown — never a
    // stale 'gone'. When the accept window expires we play the poof once; but
    // a bump that re-offers the seat restarts the countdown (expired -> false)
    // and revives the cell, so the icon never vanishes permanently (WL-005).
    setPhase((p) => (!expired ? 'live' : p === 'live' ? 'poofing' : p));
  }, [expired]);

  // The poof's own clock, in its own effect. It used to share the one above,
  // keyed on [expired, phase]: moving to 'poofing' re-ran that effect, whose
  // cleanup cancelled this very timer, so the card never reached 'gone' and
  // stayed stuck mid-poof — still labelled, and still claimable.
  useEffect(() => {
    if (phase !== 'poofing') return undefined;
    const timer = setTimeout(() => setPhase('gone'), POOF_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  if (phase === 'gone') return <>{whenGone}</>;

  const poofing = phase === 'poofing';
  return (
    <button
      type='button'
      style={style}
      className={`${styles.hand} ${styles.handJoinReady} ${count === 4 ? styles.fan4 : ''} ${
        poofing ? styles.poofing : ''
      }`}
      onClick={claimSeat}
      disabled={!claimable}
      title={`Your seat is ready · position ${entry.position}`}
      // See WaitingHand: a queued table being viewed must still carry the
      // selection marker the sliding dot anchors to.
      aria-current={isActive ? 'true' : undefined}
      aria-label='Your seat is ready, tap to join'>
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className={styles.cardBack} />
      ))}
      {!poofing && <span className={styles.countdown}>{secondsRemaining}</span>}
      <span className={styles.joinStamp}>Join</span>
      {poofing && (
        <span className={styles.poof} aria-hidden>
          <span className={styles.poofFlash} />
          {PUFF_KEYS.map((k) => (
            <span key={k} className={`${styles.poofPuff} ${styles[k]}`} />
          ))}
          {DEBRIS_KEYS.map((k) => (
            <span key={k} className={`${styles.poofDebris} ${styles[k]}`} />
          ))}
        </span>
      )}
      {/* Framing box LAST (bare only — CSS-hidden in the lobby) so it doesn't
          shift the cardBacks' :nth-child fan rules; the cards sit above it via
          their z-index. */}
      <span
        className={styles.previewFrame}
        style={{ width: previewBoxWidth(count, bare) }}
        aria-hidden
      />
      {gridDot && <GridVisibilityDot gameID={String(entry.gameId)} active={gridDot.active} />}
    </button>
  );
};

/**
 * The card a queued table shows in its own slot: the JOIN countdown once the
 * seat is actually claimable, the WAIT stamp while still in the queue. One
 * helper so the lobby strip and the bare fan rows can never disagree about
 * which of the two a given entry gets.
 */
const queuedCardFor = (entry: WaitlistEntry): React.FC<WaitlistHandProps> =>
  entry.yourTurn && entry.availableSeats > 0 ? JoinReadyHand : WaitingHand;

// Empty card placeholder. Two looks:
//   • default → face-down card back (seated active table, between deals)
//   • outline → dashed empty outline, for tables the player isn't in the hand at
//     (observing / not seated / sitting out / other away states)
// Same arch as PlayingCard so the hand silhouette reads consistently.
const PlaceholderCard: React.FC<{
  angle: number;
  z: number;
  outline?: boolean;
  width?: number;
}> = ({ angle, z, outline = false, width }) => (
  <div
    className={`${styles.card} ${outline ? styles.cardOutline : styles.cardEmpty}`}
    style={{ transform: `rotate(${angle}deg)`, zIndex: z, ...cardWidthStyle(width) }}
  />
);

// Feed the turn ring its clock colour through custom properties rather than a
// plain `borderColor`: the ring's pulse is a CSS animation, and animation
// declarations outrank inline styles, so an inline colour would be ignored on
// the animated properties. The keyframes read the vars instead.
export const turnRingStyle = (width: number, colors: TurnRingColors): CSSProperties =>
  ({
    width,
    '--yt-turn-color': colors.border,
    '--yt-turn-glow': colors.glow
  } as CSSProperties);

/**
 * The selected-table dot: one pill under the row that slides to whichever card
 * the player is viewing.
 *
 * A single shared element rather than one per card — that is what lets it
 * travel between tables instead of blinking out here and in there. It finds its
 * target by `aria-current`, the same attribute the selection already exposes to
 * assistive tech, so there is no second source of truth about which card is
 * selected.
 */
/**
 * Per-table visibility dot for the GRID view's fan tiles.
 *
 * One dot under each tile whose table is currently on the grid's screen —
 * orange for the active table (the same accent the selected count segment
 * carries), grey for the rest, absent for tables scrolled away. Opacity IS the
 * table's visible fraction, straight off the grid's scroll channel: as a table
 * slides off the edge its dot fades in proportion, which is what keeps the
 * dots moving WITH the scroll instead of blinking at some threshold (Jason
 * 2026-08-17). Written to this node directly — the channel fires per scroll
 * frame, and re-rendering the header per frame is exactly what it exists to
 * avoid.
 */
const GridVisibilityDot: React.FC<{ gameID: string; active: boolean }> = ({ gameID, active }) => {
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(
    () =>
      subscribeGridVisibility((fractions) => {
        const el = ref.current;
        if (el) el.style.opacity = String(fractions[gameID] ?? 0);
      }),
    [gameID]
  );
  // NO inline opacity: the channel subscription above is the ONLY writer.
  // Rendering opacity from React too meant every re-render (the acting tile
  // re-renders each countdown second) re-applied a value that then fought the
  // channel's per-frame writes — the visible flicker on the active dot.
  return (
    <span
      ref={ref}
      className={`${styles.gridDot} ${active ? styles.gridDotActive : ''}`}
      data-testid={`grid-dot-${gameID}`}
      aria-hidden
    />
  );
};

const SelectionDot: React.FC<{
  // False when no table is being viewed (the lobby strip) — then there is no
  // selection to mark and the dot is not rendered at all.
  enabled: boolean;
  // Changes whenever anything that could move the dot changes — the selection
  // itself, the row's composition, or its scale.
  layoutSig: string;
}> = ({ enabled, layoutSig }) => {
  // Seeded from the last placement so a remount (every table switch remounts
  // the header) starts where the dot left off and slides from there.
  const [placement, setPlacement] = useState<DotPlacement | null>(() => lastDotPlacement());
  // What is actually on screen right now. The effect MUST compare against this
  // rather than the remembered placement: it re-runs as soon as the strip
  // settles its fit, and comparing against a value it had already written to the
  // module would see "nothing changed" and skip the pending move — leaving the
  // dot latched under the table the player just left.
  const renderedRef = useRef<DotPlacement | null>(placement);
  renderedRef.current = placement;
  const dotRef = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    if (!enabled) return undefined;
    // The row is reached through the dot's OWN parent, never through a ref on
    // an ancestor: React runs a child's layout effect before it has attached
    // refs further up the tree, so an ancestor ref reads null on mount. That
    // made the dot clear itself on every fresh mount — which is every table
    // switch — and only reappear if some later change happened to re-run this.
    const container = dotRef.current?.parentElement;
    const selected = container?.querySelector<HTMLElement>('[aria-current="true"]');
    if (!selected) {
      forgetDotPlacement();
      setPlacement(null);
      return undefined;
    }
    // The tile (frame or turn ring) overhangs the button, so the dot hangs off
    // the tile's bottom edge, not the button's.
    const tile = selected.querySelector<HTMLElement>(
      `.${styles.previewFrame}, .${styles.turnRing}`
    );
    const next = dotPlacement(selected, tile);
    rememberDotPlacement(next);
    const rendered = renderedRef.current;
    if (samePlacement(rendered, next)) return undefined;
    // Nothing on screen to slide from (first appearance) — place it straight
    // away rather than burning a frame on a move with no start point.
    if (!rendered) {
      setPlacement(next);
      return undefined;
    }
    // Otherwise defer a frame so the dot paints once where it already is before
    // moving. Applying both in one commit would jump with no transition, which
    // is the whole behaviour this indicator exists for.
    const id = requestAnimationFrame(() => setPlacement(next));
    return () => cancelAnimationFrame(id);
  }, [enabled, layoutSig]);

  // Re-measure whenever the ROW's geometry changes for any reason `layoutSig`
  // cannot see — above all a window resize (Jason 2026-08-16: narrowing a wide
  // window left the dot stranded beside Exit Table). The placement is absolute
  // pixels measured from the row's origin, so any reflow that moves the cards
  // invalidates it, while layoutSig only tracks selection/composition/scale —
  // none of which change when the viewport does. A ResizeObserver catches the
  // whole class: window resizes, the strip re-fitting its cards, sibling
  // clusters growing and squeezing the band.
  useLayoutEffect(() => {
    if (!enabled || typeof ResizeObserver === 'undefined') return undefined;
    const container = dotRef.current?.parentElement;
    if (!container) return undefined;
    const observer = new ResizeObserver(() => {
      const selected = container.querySelector<HTMLElement>('[aria-current="true"]');
      if (!selected) return;
      const tile = selected.querySelector<HTMLElement>(
        `.${styles.previewFrame}, .${styles.turnRing}`
      );
      const next = dotPlacement(selected, tile);
      if (samePlacement(renderedRef.current, next)) return;
      rememberDotPlacement(next);
      setPlacement(next);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [enabled]);

  // TRACK THE SWIPE. While a table is being dragged the dot travels with it, so
  // the indicator and the felt are one movement rather than the dot waiting for
  // the switch to land and then jumping (Jason 2026-08-16: "as the second table
  // moves into frame, the dot should move with it").
  //
  // Written straight to the DOM, never through React: this fires on every drag
  // frame, and re-rendering the header per frame during a gesture is exactly
  // the work the drag channel exists to avoid. The transform is composed as
  // [settled placement] + [live drag offset], so the CSS transition still owns
  // the settle and this only borrows the element mid-gesture.
  //
  // GEOMETRY IS SNAPSHOTTED ONCE PER GESTURE, not read per frame. This
  // callback runs on EVERY offset frame (up to 120Hz), and the original
  // per-frame querySelector walks + offsetLeft reads profiled as the single
  // largest app-code cost of a swipe (~68ms of a 6-swipe loop, plus the
  // layout work the reads force between the track's transform writes). The
  // strip's slots cannot move mid-gesture - the header is stationary and the
  // row's layout only changes on a selection/composition change, which ends
  // the session - so one measurement per gesture is exact, not approximate.
  const dragGeomRef = useRef<{ nextTravel: number | null; prevTravel: number | null } | null>(null);
  useEffect(() => {
    if (!enabled) return undefined;
    return subscribeDragOffset((dx) => {
      const el = dotRef.current;
      const base = renderedRef.current;
      if (!el || !base) return;
      if (dx === 0) {
        // Released and fully at rest — hand the element back to its transition.
        dragGeomRef.current = null;
        el.style.transition = '';
        el.style.transform = `translate(${base.left}px, ${base.top}px)`;
        return;
      }
      // Track the finger while the gesture is still open — a live drag, or the
      // spring returning a failed one. NOT a committed settle: the moment a
      // commit lands the table changes, this header remounts around the new
      // selection, and "the neighbour in the drag's direction" starts meaning
      // the table one PAST the destination. Tracking through that would sail
      // the dot clean past the table it is marking. The commit case is handed
      // to the CSS transition instead, which glides it from wherever the drag
      // left it (rememberDotPlacement below is what preserves that start
      // point across the remount) to the newly selected button.
      if (!(getDragState().phase === 'dragging' || isSpringingBack())) {
        dragGeomRef.current = null;
        el.style.transition = '';
        return;
      }
      let geom = dragGeomRef.current;
      if (!geom) {
        const container = el.parentElement;
        const selected = container?.querySelector<HTMLElement>('[aria-current="true"]');
        if (!container || !selected) return;
        const slots = Array.from(container.querySelectorAll<HTMLElement>(`.${styles.hand}`));
        const here = slots.indexOf(selected);
        if (here === -1) return;
        // dx < 0 drags the felt left, bringing the NEXT table in from the right.
        geom = {
          nextTravel: slots[here + 1] ? slots[here + 1].offsetLeft - selected.offsetLeft : null,
          prevTravel: slots[here - 1] ? slots[here - 1].offsetLeft - selected.offsetLeft : null
        };
        dragGeomRef.current = geom;
      }
      // Lerp between this button's centre and the neighbour's. No neighbour
      // (an edge) means the dot simply holds — matching the felt, which is
      // rubber-banding rather than travelling.
      const travel = dx < 0 ? geom.nextTravel : geom.prevTravel;
      if (travel === null) return;
      // How far the drag has carried the felt, 0..1 of one table's width.
      const progress = Math.min(1, Math.abs(dx) / (window.innerWidth || 1));
      const live = base.left + travel * progress;
      el.style.transition = 'none'; // the finger owns it; no easing to fight
      el.style.transform = `translate(${live}px, ${base.top}px)`;
      // Remember where the dot ACTUALLY is, not where it settled last. A commit
      // remounts this header mid-gesture, and the replacement seeds itself from
      // this value — without it the dot would snap back to the old button and
      // then glide, instead of continuing from where the drag had carried it.
      rememberDotPlacement({ ...base, left: live });
    });
  }, [enabled]);

  if (!enabled) return null;
  // Always rendered once enabled — the element has to exist for the effect above
  // to find the row, and keeping it mounted is also what lets the transition
  // carry it between tables instead of blinking out and back in. Before it has
  // been placed it is simply invisible.
  return (
    <span
      ref={dotRef}
      className={styles.selectionDot}
      style={
        placement
          ? {
              transform: `translate(${placement.left}px, ${placement.top}px)`,
              width: placement.width
            }
          : { opacity: 0 }
      }
      data-testid='table-selection-dot'
      aria-hidden
    />
  );
};

// Live "seconds to act" on the turn clock, ticking down in lockstep with the
// seat timer (remaining = total − elapsed). Anchored to a wall-clock end time so
// the per-second tick doesn't drift, and it re-syncs whenever `total`/`elapsed`
// change (a server `time` tick / a new turn). `enabled` gates the interval so
// non-acting previews never run a timer. Ticks all the way to 0 (and stays
// there) so callers can react to the timeout. Returns null when inactive.
const useTurnCountdown = (
  totalSeconds: number | null | undefined,
  elapsedSeconds: number | null | undefined,
  enabled: boolean
): number | null => {
  const [secs, setSecs] = useState<number | null>(null);
  const total = totalSeconds ?? 0;
  const elapsed = elapsedSeconds ?? 0;
  const active = enabled && total > 0;

  useEffect(() => {
    if (!active) {
      setSecs(null);
      return undefined;
    }
    const endAt = Date.now() + Math.max(0, total - elapsed) * 1000;
    const tick = () => setSecs(Math.max(0, Math.ceil((endAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [active, total, elapsed]);

  return secs;
};

// One table's hand. Tracks its own hover so the fan can widen smoothly (the
// arch angles are recomputed wider while hovered — works for both the 2-card
// Hold'em and 4-card Omaha hands, unlike the old per-nth-child CSS rules).
const Hand: React.FC<{
  table: ActiveTable;
  onOpen?: (gameID: string, game: string) => void;
  // Max arch angle for Omaha (4-card) fans — lowered by the "tighten" layout
  // mode to keep the wide fan from overlapping its neighbours.
  omahaMax?: number;
  // Per-hand layout override (e.g. the computed overlap margin in bare mode).
  style?: CSSProperties;
  // The table currently being viewed is the one the selection dot sits under.
  isActive?: boolean;
  // In-table previews (bare) surface an animated "your turn" countdown ring + an
  // ACT stamp on the table awaiting the player's action. The lobby strip leaves
  // this off (it already has the pulse dot) so its layout is unchanged.
  showTurnIndicator?: boolean;
  // The player's waitlist entry for this table, when they're queued on a table
  // they also have open (e.g. observing while waiting). Renders a compact
  // "WAIT · N" pill on the fan — or a live "JOIN {secs}" countdown once the seat
  // is ready — in place of the old "#N"/WAITLIST stamp.
  waitlistEntry?: WaitlistEntry;
  // Grid-header / bare previews use a slightly larger framing box (more padding
  // around the same-size cards) than the lobby strip.
  bare?: boolean;
  // Narrower cards for four-card (Omaha) hands — see STRIP_OMAHA_CARD_WIDTH.
  wideHandCardWidth?: number;
  // GRID view: render this tile's visibility dot (see GridVisibilityDot).
  gridDot?: { active: boolean };
}> = ({
  table: t,
  onOpen,
  omahaMax = 22,
  style,
  isActive = false,
  gridDot,
  showTurnIndicator = false,
  waitlistEntry,
  bare = false,
  wideHandCardWidth
}) => {
  const [hovered, setHovered] = useState(false);
  // Only wire hover on devices that truly hover. On iOS the first tap fires an
  // emulated mouseenter (which fans the cards) and SUPPRESSES the click that
  // would open the table — forcing a second tap. Gating the handlers off makes
  // the first tap open the table directly. (See game-client-performance.md.)
  const supportsHover = useSupportsHover();
  // ONE STATUS SLOT, decided once per hand. Reading the fields live was still
  // wrong even as states: the engine does not clear them together, so the tile
  // played the resolution as a sequence of half-states — WINNER, then ALL IN
  // again, then the cards flashing back. useHandOutcome pins the outcome to the
  // hand number and freezes the cards at that moment; the next hand clears it
  // by existing.
  const { outcome, cardsAtResolve } = useHandOutcome({
    gameID: String(t.gameID),
    handNo: t.handNo,
    isWinner: Boolean(t.isWinner),
    isFolded: Boolean(t.isFolded),
    isShowdownLoss: Boolean(t.isShowdownLoss),
    isHandLive: Boolean(t.isHandLive),
    isHandIdle: Boolean(t.isHandIdle),
    cards: t.cards
  });
  const showWinner = outcome === 'win';
  const showFold = outcome === 'fold';
  // Out of the hand, however it happened. The dim is the whole cue for a
  // showdown loss — no badge, because "you lost" is not something the player
  // needs the dock to say, and it would be the one place it speaks without
  // being useful.
  const isOut = outcome === 'fold' || outcome === 'lost';
  // All in is only news while the hand is still live. Once it has resolved the
  // outcome is the news, and ALL IN reappearing after WINNER was the step that
  // made no sense.
  const showAllIn = Boolean(t.isAllIn) && outcome === null;

  // See useHandOutcome: once a hand has resolved, the tile shows the hand as it
  // STOOD, not whatever the next broadcast happens to carry. Without this the
  // cards flashed back on screen after the fold had already taken them away.
  const shownCards = (outcome ? cardsAtResolve : t.cards) as typeof t.cards;
  const hasCards = shownCards !== null;
  const count = hasCards ? shownCards!.length : t.game === 'omaha' ? 4 : 2;
  const angles = cardAngles(count, hovered ? 1.35 : 1, omahaMax);
  // Four-card hands only: narrower cards let all four read inside the same slot
  // without fanning the arch so wide that it collides with the next table's.
  const cardWidth = count >= 4 ? wideHandCardWidth : undefined;

  const isWaitlisted = !!waitlistEntry && waitlistEntry.position > 0;
  const waitReady = !!waitlistEntry && waitlistEntry.yourTurn && waitlistEntry.availableSeats > 0;
  // Live seconds for the ready ("JOIN") pill. Called unconditionally (null start
  // → inactive) so hook order is stable.
  const { secondsRemaining: waitSecs, active: waitActive } = useWaitlistCountdown({
    waitForAcceptStartTime: waitlistEntry?.waitForAcceptStartTime,
    waitlistTimeOut: waitlistEntry?.waitlistTimeOut ?? 0,
    bumpCount: waitlistEntry?.bumpCount
  });
  const statusText = t.statusLabel || '';
  const titleStakes = t.stakes ? `${t.stakes} ` : '';
  const stateLabel = isWaitlisted
    ? ` (on waitlist, position ${waitlistEntry?.position})`
    : statusText
    ? ` (${statusText.toLowerCase()})`
    : !t.isSeated
    ? ' (observing)'
    : t.isFolded
    ? ' (folded)'
    : '';
  // Surface the "your turn" state to AT (visually shown by the ring + ACT stamp).
  // An away-state (sitting out / standing up / etc.) means the player isn't acting.
  const isActing = showTurnIndicator && t.isMyTurn && !isWaitlisted && !statusText;
  // EN-10237: this table is waiting on the player's multi-run vote. Yields to
  // the same table conditions `isActing` does — a player on the waitlist or
  // sitting out is not being asked anything. `voteClock`'s presence is the
  // whole signal; useActiveTables owns the eligibility rule.
  const isVoting = showTurnIndicator && !!t.voteClock && !isWaitlisted && !statusText;
  // ONE STAMP, chosen here rather than by whichever JSX branch happened to
  // render last. Two of them painted at once in the lobby's Your Tables row —
  // FOLD sitting on top of SITTING OUT — because each stamp guarded itself and
  // the new ones did not know about the old.
  //
  // The order is TABLE conditions before HAND conditions. How you stand with
  // the table outranks what happened in a hand: if you are sitting out, FOLD is
  // not merely redundant, it is wrong — you were never in that hand. `isActing`
  // already encoded this rule (it yields to waitlist and away-states); the rest
  // now follow it instead of restating it.
  //
  // Within each group, urgency: a seat going cold (JOIN) before a queue
  // position, and being asked to act before anything already settled.
  //
  // VOTE sits at the top of the hand group, ahead of ACT: the chips are already
  // in, the window is a few seconds long, and unlike a turn there is no
  // fold-by-default that leaves the player where they expected to be.
  const stamp: 'join' | 'wait' | 'status' | 'vote' | 'act' | 'winner' | 'allin' | 'fold' | null =
    isWaitlisted && waitReady
      ? 'join'
      : isWaitlisted
      ? 'wait'
      : showTurnIndicator && statusText
      ? 'status'
      : isVoting
      ? 'vote'
      : isActing
      ? 'act'
      : showWinner
      ? 'winner'
      : showAllIn
      ? 'allin'
      : showFold
      ? 'fold'
      : null;

  const turnLabel = isVoting
    ? ' — waiting on your multi-run vote'
    : isActing
    ? ' — your turn to act'
    : '';
  // Ring hugs the resting fan width (Omaha ~65px, Hold'em ~56px).
  const ringWidth = previewBoxWidth(count, bare);
  // Tournament tables (SnG 't' / MTT 'm') get a trophy badge.
  const isTournament = t.tabletype === 't' || t.tabletype === 'm';
  // Not in the current hand (observing / not seated / sitting out / other away
  // state) → render the cards as dashed empty outlines instead of card backs.
  const isInactive = !t.isSeated || !!statusText || !!t.isHandIdle;
  // Seated, hand finished with no cards (e.g. everyone folded / showdown over) →
  // show a "- - - -" placeholder instead of face-down backs.
  // THE DASHES ARE GONE, deliberately. The card area answers one question —
  // what am I holding here — and it had four ways of answering it: real cards,
  // face-down backs, a `----` dash row, and dashed outline placeholders. The
  // dashes and the outlines said the SAME thing ("nothing in hand") in two
  // different visual languages, and the dashes said it only inside a narrow
  // window (hand over, seated, cards gone). Two symbols for one meaning is a
  // thing to learn twice.
  //
  // Three answers now, each meaning exactly one thing:
  //   real cards      — you hold these
  //   face-down backs — dealt in, cards not shown here
  //   dashed outline  — no hand right now
  //
  // It also removes a flash: the dashes were a transient middle state on the
  // way from cards to backs, so the tile blinked through them every hand.

  // Live countdown for the acting table — drives the number and the ring colour.
  const countdownSecs = useTurnCountdown(
    t.turnClock?.totalSeconds,
    t.turnClock?.elapsedSeconds,
    isActing && !!t.turnClock
  );
  // The vote window rides the SAME countdown hook — it is a server-anchored
  // (total, elapsed) window like the turn clock, just with a different total —
  // so the two can never tick differently. Called unconditionally (inactive
  // when there is no vote) so hook order stays stable.
  const voteTick = useTurnCountdown(
    t.voteClock?.totalSeconds,
    t.voteClock?.elapsedSeconds,
    isVoting && !!t.voteClock
  );
  // useTurnCountdown seeds itself in an EFFECT, so it is null on the first
  // paint — and this pill mounts at the moment the window opens, so that frame
  // is not hypothetical. Rendering `?? 0` there flashes "0", which reads as
  // "expired" on the one cue whose whole job is to say "you still have time".
  // turnSecondsLeft is the existing fallback for precisely this: prefer the
  // live tick, fall back to the clock's own remaining. (The turn ring already
  // uses it below, for the same reason.)
  const voteSecs = turnSecondsLeft(voteTick, t.voteClock);
  // Ring colour for the acting table, tracking the clock it shares with the seat
  // timer on the felt. Seeded from `turnClock` rather than the countdown alone
  // so a table switch (which remounts this subtree) doesn't paint the ring green
  // for a frame before the first tick lands — see turnSecondsLeft.
  const dockOutline = useDockOutline();
  // Tint the EXISTING turn ring rather than adding a second contour — and hold
  // it static, since a pulse here would compete with the shot clock's own.
  const outcomeRing =
    dockOutline === 'ring' && !isActing
      ? showWinner
        ? { border: '#bdff94', glow: 'rgba(98,139,72,0.9)' }
        : showAllIn
        ? { border: 'rgba(255,120,60,1)', glow: 'rgba(200,40,40,0.75)' }
        : null
      : null;
  // ONE countdown, whichever clock is asking. A turn and a vote window are the
  // same thing to the player — the table is waiting on an answer and there are
  // this many seconds of it left — so they drive the SAME ring and the SAME
  // digit, on the seat timer's ramp either way. The vote used to say it with a
  // "VOTE" word beside a small digit; the countdown was always the answerable
  // half.
  //
  // THE VOTE IS READ FIRST, and that order is load-bearing rather than
  // stylistic: `isMyTurn` does NOT drop during a vote window (isLiveBettingTurn
  // clears on `isMultiRunActive`, which only goes true once the RUN-OUT starts,
  // and CHECK_MULTI_RUN does not clear `startTurn`), so the seat that made the
  // last betting action has isActing AND isVoting true at once. Reading the turn
  // first counted that tile down from ~30s of `game.speed` under a label saying
  // "Multi-run vote, 30 seconds left" while the real window was ~10. Same
  // precedence `stamp` and `turnLabel` already apply, for the same reason.
  const pendingSecs = isVoting
    ? voteSecs
    : isActing
    ? turnSecondsLeft(countdownSecs, t.turnClock)
    : null;
  const pendingTotal = isVoting ? t.voteClock?.totalSeconds : t.turnClock?.totalSeconds;
  const turnColors = turnRingColors(pendingSecs, pendingTotal);
  // A closing table the player isn't seated at, or a Sit & Go registration
  // whose table isn't known yet, has nothing to open.
  const cannotOpen = Boolean((t.pendingShutdown && !t.isSeated) || t.notOpenable);

  return (
    <button
      type='button'
      style={style}
      className={`${styles.hand} ${t.isMyTurn ? styles.handUrgent : ''} ${
        isOut ? styles.handFolded : ''
      }`}
      onMouseEnter={supportsHover ? () => setHovered(true) : undefined}
      onMouseLeave={supportsHover ? () => setHovered(false) : undefined}
      onClick={() => (cannotOpen ? undefined : onOpen?.(t.gameID, t.game))}
      disabled={cannotOpen}
      title={`${titleStakes}${t.label}${stateLabel}`}
      // The table you are currently playing is the "current page" of the strip:
      // sighted players see the dot beneath it, AT gets aria-current — which is
      // also what the dot itself homes in on, so there is one source of truth.
      // Only one
      // card carries it (isActive is derived from a single activeGameID).
      aria-current={isActive ? 'true' : undefined}
      aria-label={`Open ${t.label} ${t.stakes}${stateLabel}${turnLabel}`}>
      {t.pendingShutdown && (
        <span className={styles.pendingShutdownStamp}>{PENDING_SHUTDOWN_LABEL}</span>
      )}
      {/* The tile says only what the TABLE is doing — a neutral frame when idle,
          the shot-clock colour when it wants the player's action (green →
          yellow → orange → red, the same ramp the seat timer runs; see
          turnRingColor.ts). Which table the player is VIEWING is marked outside
          the tile by the sliding dot, so the two signals never compete for the
          same contour. */}
      {isActing || isVoting ? (
        <span
          className={styles.turnRing}
          style={turnRingStyle(ringWidth, turnColors)}
          aria-hidden
        />
      ) : // Outcome ring — the A/B variant. Same element the shot clock colours,
      // so the tile keeps exactly one contour (see dockCuePrefs).
      outcomeRing ? (
        <span
          className={styles.turnRing}
          style={turnRingStyle(ringWidth, outcomeRing)}
          aria-hidden
        />
      ) : (
        // Default: a neutral box framing the fan (shown only in the bare
        // grid-header / switcher context — CSS-hidden in the lobby strip).
        <span className={styles.previewFrame} style={{ width: ringWidth }} aria-hidden />
      )}
      {gridDot && <GridVisibilityDot gameID={String(t.gameID)} active={gridDot.active} />}
      {hasCards
        ? shownCards!.map((c, i) => (
            <PlayingCard
              key={i}
              rank={c.rank}
              suit={c.suit}
              angle={angles[i]}
              z={i}
              width={cardWidth}
            />
          ))
        : angles.map((a, i) => (
            <PlaceholderCard
              key={i}
              angle={a}
              z={i}
              outline={showTurnIndicator && isInactive}
              width={cardWidth}
            />
          ))}
      {/* Tournament (SnG / MTT) marker — a small trophy badge. */}
      {showTurnIndicator && isTournament && (
        <span className={styles.tourneyBadge} aria-label='Tournament table'>
          <EmojiEventsIcon style={{ fontSize: 12 }} />
        </span>
      )}
      {/* Countdown number — seconds left on whichever clock is pending (turn or
          multi-run vote), centered on the fan. */}
      {pendingSecs !== null && (
        <span
          className={styles.turnCountdown}
          aria-label={
            isVoting
              ? `Multi-run vote, ${pendingSecs} seconds left`
              : `${pendingSecs} seconds to act`
          }>
          {pendingSecs}
        </span>
      )}
      {/* "ACT" stamp on the table awaiting the player's action (in-table
          previews only) — same placement language as the Wait/Join stamps. */}
      {/* THE STATUS SLOT — exactly one occupant, resolved above. */}
      {stamp === 'join' && (
        <span className={`${styles.waitPill} ${styles.waitPillJoin}`}>
          <span className={styles.waitPillCount}>{waitActive ? waitSecs : 0}</span>
          JOIN
        </span>
      )}
      {stamp === 'wait' && (
        <span className={styles.waitPill}>WAIT · {waitlistEntry?.position}</span>
      )}
      {stamp === 'status' && <span className={styles.statusStamp}>{statusText}</span>}
      {/* 'vote' renders NOTHING here, and still holds the slot. The ring and
          digit above are the whole cue now — the "VOTE" word is gone — but the
          slot must stay claimed: without it a vote window would fall through to
          ALL IN (true by definition during one) and the tile would report a
          settled fact instead of the pending question. */}
      {stamp === 'act' && <span className={styles.actStamp}>ACT</span>}
      {stamp === 'winner' && <span className={styles.winnerStamp}>WINNER</span>}
      {stamp === 'allin' && <span className={styles.allInStamp}>ALL IN</span>}
      {stamp === 'fold' && <span className={styles.foldStamp}>FOLD</span>}
      {/* Orbs ride the outcome, not the slot: they are the celebration, and the
          celebration is still true even when a table condition is doing the
          talking. */}
      {(showWinner || showAllIn) && (
        <span className={styles.orbs} aria-hidden>
          {ORB_KEYS.map((k) => (
            <i
              key={k}
              className={`${styles.orb} ${showWinner ? styles.orbWin : styles.orbAllIn}`}
            />
          ))}
        </span>
      )}
    </button>
  );
};

// --- Bare (in-table overlay) fan geometry, used by the comparison modes ----
// Resting visual width of a fan's cards (measured): Hold'em (2) ≈ 56px, Omaha
// (4) ≈ 87px, inside the fixed 88px .hand box. The leftover padding per side is
// what a negative margin can safely eat before it starts biting into cards.
// Exported so the panel's max-width test can re-derive that width from the real
// box rather than restating 88 (YourTables.width.test.ts).
export const HAND_BOX = 88;
// In-table previews render slightly smaller than the lobby strip (applied on top
// of the fit scale). 1 = same size as the lobby. Exported as the dock's base
// size so the strip-ceiling contract below is testable.
export const BARE_BASE_SCALE = 0.85;
// The navigation strip skips that shrink: it is the player's control tower on a
// phone, it owns the top band outright (the table meta moved to the felt's
// bottom-right corner to free it), and its cards are tap targets that need to
// stay readable. The fit-scale still shrinks the row when the band is too
// narrow for the open-table count, so this is a CEILING, not a fixed size.
const STRIP_BASE_SCALE = 1;

/**
 * The strip's effective scale, ceilinged at the DOCK size (EN-10545 — Jason
 * 8/21): one dock, one size at every width. Below NARROW_MAX_PX the single
 * view swaps its dock band for this strip, and the strip's own ceiling of 1
 * let a comfortable band (~600–795px) render the tiles 18% LARGER than the
 * dock they replace — the dock visibly growing as the window narrows. The
 * ceiling is now BARE_BASE_SCALE, so the strip matches the dock wherever the
 * band allows it and still shrinks below when a phone band is genuinely too
 * narrow (a 58px tile at 0.85 is ~49px — above the 44px tap minimum).
 */
export const stripEffectiveScale = (fitScale: number): number =>
  Math.min(fitScale * STRIP_BASE_SCALE, BARE_BASE_SCALE);
// Resting visual width of a fan's cards inside the 88px box (measured). The
// in-table fans use the tightened Omaha arch (OMAHA_MAX below), so a 4-card
// fan spans ~65px and Hold'em ~56px. The leftover side padding is what the
// overlap margin can eat before it bites into the cards.
//
// Since the deck (EN-10656) a card is 25.4px wide and its only identity is the
// top-left index, so every card in a fan has to expose ~11px — the same
// legibility floor the strip uses (RANK_LEGIBLE_PX in the navStrip test). At
// the old 12° arch the next card covered the index (only ~7px showed); 20°
// exposes 20 × 2 / 3 × 0.88 ≈ 11.7px per card, and the 4-card fan spans
// 2 × 20 × 0.88 + 25.4 ≈ 61px — still narrower than the old 36px cards were
// at 12° (~57px + shadow), so it stays inside the 65px budget above.
const OMAHA_MAX = 20;
// --- Omaha in the strip -----------------------------------------------------
// Four cards in one slot is the tight case. At the in-table 12° arch they very
// nearly coincided and the top card hid the rest — only two of the four hole
// cards were readable. Opening the arch to the lobby's 22° fixed that but made
// the fan wide enough to run into the neighbouring table's.
//
// Narrower CARDS solve both: four slimmer cards separate at a modest arch and
// the whole fan stays comfortably inside its slot. Height is untouched, so the
// fan's baseline — and everything aligned to it — stays put.
//
// The arch is then set by what has to REMAIN VISIBLE: each card is overlapped
// from the right by the next one, so the rank/suit corner (left: 4px, ~11px
// glyph → ~16px of card) must still be exposed. Cards pivot at 120% of their
// height below centre (transform-origin: 50% 170%), so one degree of rotation
// buys ≈ 0.88px of horizontal offset:
//
//   step between cards = 2 × max ÷ 3 = 16°  →  ~14px exposed per card
//   full fan span      = 2 × max × 0.88 + card width ≈ 68px, inside the ~79px
//                        slot pitch, so it still clears the neighbouring table
// The arch is set by what has to REMAIN VISIBLE: each card is overlapped from
// the right by the next one, so its rank/suit corner (left: 4px, ~11px glyph →
// ~16px of card) must stay exposed. Cards pivot at 120% of their height below
// centre (transform-origin: 50% 170%), so one degree of rotation buys ≈0.88px
// of horizontal offset:
//
//   step between cards = 2 × max ÷ 3 = 16°  →  ~14px exposed per card
//   full fan span      = 2 × max × 0.88 + card width ≈ 68px, inside the ~79px
//                        slot pitch, so it still clears the neighbouring table
const STRIP_OMAHA_MAX = 21;
// Since the deck fixed the card's shape (EN-10656) the .card default is already
// 25.4px wide at the 42px row height, so this now pins a near-identical card;
// kept as the strip's explicit Omaha width.
const STRIP_OMAHA_CARD_WIDTH = 26;
const cardSpan = (count: number): number => (count >= 4 ? 65 : 56);
const sidePad = (count: number): number => (HAND_BOX - cardSpan(count)) / 2;
const cardCountOf = (t: ActiveTable): number =>
  t.cards ? t.cards.length : t.game === 'omaha' ? 4 : 2;
// Target visual gap between adjacent fans (px). Bumped +10 so the enlarged
// grid-header / overlay framing boxes (the `bare` ringWidth widening) don't
// overlap their neighbours.
const TARGET_GAP = 14;
// The trailing "+" add-table slot draws a single card-sized dashed box (matches
// the .card width) centered in the standard HAND_BOX, so it tucks into the row
// the same way a 1-card "fan" would.
// Visible width of the add-table control. Must track `.addSlotInner`'s CSS
// width — the spacing math positions the row from what is DRAWN, so a mismatch
// here shows up as an off-centre gap beside the "+".
export const ADD_SLOT_SPAN = 44;

/**
 * The gap the player actually SEES between two neighbouring tiles, and between
 * the "+" and the first tile. One number, everywhere, at every viewport width.
 *
 * The bare rows used to derive their overlap from each neighbour's CARD COUNT
 * (`sidePad`), targeting a constant gap between the FANS. But what a player sees
 * is the framing tile, which is wider than the fan inside it — and wider still
 * for a four-card Omaha hand — so the visible gap silently changed with the mix
 * of games open and the row never looked evenly spaced (Jason 2026-08-17).
 * Solving for the tile edges makes the thing held constant the thing looked at.
 */
export const TILE_GAP = 12;

/**
 * Height of a `.hand` slot in the bare rows (see YourTables.module.scss).
 *
 * Trailing content is held to this so it does not inflate the row. A tile is
 * 58px but ABSOLUTELY positioned, overhanging its 44px slot — it contributes no
 * height. A 58px trailing element in normal flow does, which pushed the row to
 * 58, re-centred every hand inside it, and left the trailing element sitting
 * visibly lower than the tiles it was supposed to line up with (Jason
 * 2026-08-17). Matching the slot and letting the content overhang makes it obey
 * the same rule as everything else in the row.
 */
export const HAND_SLOT_HEIGHT = 44;

/**
 * Negative margin that leaves exactly TILE_GAP of visible space between two
 * boxes of the given drawn widths, each centred in a HAND_BOX-wide slot.
 *
 * Each box is centred in its slot, so the space between them is the slot pitch
 * less half of each box. Rearranged for the margin:
 *
 *     margin = gap + (leftWidth + rightWidth) / 2 - HAND_BOX
 */
const gapMargin = (leftWidth: number, rightWidth: number): number =>
  TILE_GAP + (leftWidth + rightWidth) / 2 - HAND_BOX;
// Minimum mobile tap target (pt ≈ CSS px here). The .hand / .addSlot boxes are
// already larger (88 × 44/45), so this is a floor that guarantees the strip's
// targets can never be squeezed under it by a layout change.
const TAP_TARGET_MIN = 44;

// --- Strip geometry: FIXED, so switching tables never resizes anything -------
// The centered fan rows size themselves to their content: the overlap margin is
// padding-aware per neighbour pair, and the fit-scale divides the band by the
// row's natural width. In the strip that would be a bug — a hand going from 2
// hole cards to 4 (or simply switching to a table whose cards have arrived)
// changes the natural width, and every card in the strip resizes and reflows
// under the player's thumb. So the strip pins both to the WORST case instead:
//
//   • one fixed slot pitch, sized for the widest fan (Omaha, 4 cards), and
//   • a natural width derived from the table CAP, not from what is open now.
//
// The result: a card slot is always the same size and always in the same place;
// opening or closing a table changes how many slots are filled, nothing else.
const WIDEST_FAN_CARDS = 4;
// Negative overlap eats the empty padding the two neighbouring boxes share,
// leaving TARGET_GAP of visible space between the fans themselves.
const stripCellMargin = (): number => -(2 * sidePad(WIDEST_FAN_CARDS) - TARGET_GAP);

// What the player actually SEES in a strip slot is the framing tile, which is
// wider than the fan it holds — so the gap between two slots is the pitch less a
// whole tile, not TARGET_GAP.
const STRIP_TILE_SPAN = previewBoxWidth(WIDEST_FAN_CARDS, true);
export const stripSlotPitch = (): number => HAND_BOX + stripCellMargin();
export const stripVisibleGap = (): number => stripSlotPitch() - STRIP_TILE_SPAN;

/**
 * Margin that tucks the "+" against the card cluster.
 *
 * Derived so the space beside the "+" is the SAME space that separates two
 * tables. Matching the slot pitch instead would leave a much wider hole, because
 * the "+" box is far narrower than a tile and its slot's leftover padding shows
 * as empty felt — the add control read as a detached button rather than the next
 * seat in the row, which is confusing next to the "+" that adds chips.
 */
const stripAddMargin = (): number =>
  (ADD_SLOT_SPAN + STRIP_TILE_SPAN) / 2 + stripVisibleGap() - HAND_BOX;

// Widest row the strip can ever draw: either every slot filled (at the cap, no
// "+"), or one fewer slot plus the "+". Independent of what is open right now,
// which is the whole point.
// Fallback when a host doesn't pass the cap — the layout carries five slots.
const DEFAULT_STRIP_CAPACITY = 5;

/**
 * The strip's last computed fit, remembered across mounts.
 *
 * The strip lives in the single-table header, which is part of the per-table
 * subtree — so switching tables remounts it. Recomputing from scratch paints
 * one frame at scale 1 before the measurement lands, which reads as the strip
 * zooming in and out on every swipe. Its geometry is derived from the table CAP
 * (not from what is open), so for a given band the fit is a constant: the
 * previous value is the right one to paint immediately.
 */
let rememberedStripFit: { scale: number; dx: number } = { scale: 1, dx: 0 };
const lastStripFit = () => rememberedStripFit;
export const forgetStripFit = (): void => {
  rememberedStripFit = { scale: 1, dx: 0 };
};

export const stripNaturalWidth = (capacity: number): number => {
  const slots = Math.max(1, capacity);
  const cellsRow = (n: number) =>
    n <= 0 ? 0 : HAND_BOX + (n - 1) * (HAND_BOX + stripCellMargin());
  const full = cellsRow(slots);
  const withAdd = cellsRow(slots - 1) + HAND_BOX + (slots - 1 > 0 ? stripAddMargin() : 0);
  return Math.max(full, withAdd);
};

// The in-table fan row (the lobby strip never uses this). The fans use a
// tightened Omaha arch, a padding-aware overlap, and a fit/center-clamp so the
// row stays page-centered until it would hit a side button cluster, then slides
// left and finally scales down — never overlapping the buttons.
const BareFans: React.FC<{
  // The player's open tables, in the shared subscription order. Rendered as
  // handed — a queued one shows its waitlist card in its own slot (see
  // inlineWaitlist), it is not filtered out.
  tables: ActiveTable[];
  onOpenTable?: (gameID: string, game: string) => boolean | void;
  activeGameID?: string;
  onAddTable?: () => void;
  // Waitlist cards for tables that are NOT open, so they have no slot in
  // `tables` to sit in. Ready seats lead the row, still-queued trail it — the
  // same bookending the lobby strip gives them.
  readyEntries?: WaitlistEntry[];
  waitingEntries?: WaitlistEntry[];
  // Waitlist cards for tables that ARE in `tables` — rendered in that table's
  // own slot instead of being pulled out to the end of the row. Every row is a
  // positional map of the player's tables (EN-10545): joining a waitlist is not
  // opening or closing a table, so it must not move any card.
  // Keyed by String(gameId).
  inlineWaitlist?: Record<string, WaitlistEntry>;
  // Play the staggered entrance (switcher open).
  animateIn?: boolean;
  // Skip the page-centering self-shift (parent centers the group instead).
  autoCenter?: boolean;
  // Mobile navigation-strip layout: centred group, "+" leading the cards.
  stripLayout?: boolean;
  // Table cap (MAX_OPEN_TABLES) — the strip sizes its slots for a FULL strip so
  // its geometry never depends on how many tables happen to be open.
  stripCapacity?: number;
  /** GRID view visibility dots — see the outer component's prop of this name. */
  gridViewDots?: boolean;
  /** Rendered after the last tile, at TILE_GAP from its drawn edge. */
  trailing?: React.ReactNode;
}> = ({
  tables,
  onOpenTable,
  activeGameID,
  onAddTable,
  readyEntries = [],
  waitingEntries = [],
  inlineWaitlist = {},
  animateIn,
  autoCenter = true,
  stripLayout = false,
  stripCapacity = DEFAULT_STRIP_CAPACITY,
  gridViewDots = false,
  trailing
}) => {
  // Per-cell layout style. Every mode carries the padding-aware overlap margin;
  // the strip additionally floors each card at the minimum mobile tap target.
  const cellStyle = (marginLeft?: number): CSSProperties | undefined => {
    const margin = marginLeft === undefined ? undefined : { marginLeft };
    if (!stripLayout) return margin;
    return { ...margin, minWidth: TAP_TARGET_MIN, minHeight: TAP_TARGET_MIN };
  };

  // Unify the row into ordered cells: orphan ready seats, then the player's
  // tables in subscription order (each either a hand or its own waitlist card),
  // then orphan queued entries. Each cell carries the card count the overlap/fit
  // math needs and a factory that renders it with the computed left margin.
  const cells: {
    key: string;
    cardCount: number;
    render: (marginLeft?: number) => React.ReactNode;
  }[] = [
    ...readyEntries.map((e) => ({
      key: `wl-${e.gameId}`,
      cardCount: cardBackCount(e.gameType),
      render: (marginLeft?: number) => (
        <JoinReadyHand
          key={`wl-${e.gameId}`}
          entry={e}
          onOpenTable={onOpenTable}
          // gameIds arrive as numbers here and strings from state.tables —
          // compare as strings, same as the seated hands below.
          isActive={!!activeGameID && String(e.gameId) === String(activeGameID)}
          bare
          style={cellStyle(marginLeft)}
        />
      )
    })),
    ...tables.map((t) => {
      // gameIDs arrive as strings from state.tables and numbers from some
      // payloads — compare as strings so the highlight never misses.
      const isActive = !!activeGameID && String(t.gameID) === String(activeGameID);
      const renderHand = (marginLeft?: number) => (
        <Hand
          key={t.id}
          table={t}
          onOpen={onOpenTable}
          omahaMax={stripLayout ? STRIP_OMAHA_MAX : OMAHA_MAX}
          wideHandCardWidth={stripLayout ? STRIP_OMAHA_CARD_WIDTH : undefined}
          isActive={isActive}
          gridDot={gridViewDots ? { active: isActive } : undefined}
          showTurnIndicator
          bare
          style={cellStyle(marginLeft)}
        />
      );
      // A table the player is queued on (not seated) shows its waitlist card
      // HERE, in its own slot — not appended after every hand. Joining a queue
      // is not opening or closing a table, so no card may move (EN-10545).
      const queued = inlineWaitlist[String(t.gameID)];
      if (queued) {
        const Card = queuedCardFor(queued);
        return {
          key: t.id,
          cardCount: cardBackCount(queued.gameType),
          render: (marginLeft?: number) => (
            <Card
              key={t.id}
              entry={queued}
              onOpenTable={onOpenTable}
              isActive={isActive}
              // This table HAS a grid cell (it is in `tables`), so in grid view
              // its tile carries a visibility dot exactly like a seated hand's —
              // same gate, same active test. Orphan waitlist entries get none:
              // they have no cell, so the dot could only ever be blank.
              gridDot={gridViewDots ? { active: isActive } : undefined}
              // The open table's own guard and tile carry over to its card.
              disabled={Boolean(t.pendingShutdown && !t.isSeated)}
              whenGone={renderHand(marginLeft)}
              bare
              style={cellStyle(marginLeft)}
            />
          )
        };
      }
      return { key: t.id, cardCount: cardCountOf(t), render: renderHand };
    }),
    ...waitingEntries.map((e) => ({
      key: `wl-${e.gameId}`,
      cardCount: cardBackCount(e.gameType),
      render: (marginLeft?: number) => (
        <WaitingHand
          key={`wl-${e.gameId}`}
          entry={e}
          onOpenTable={onOpenTable}
          isActive={!!activeGameID && String(e.gameId) === String(activeGameID)}
          bare
          style={cellStyle(marginLeft)}
        />
      )
    }))
  ];
  const cellSig = cells.map((c) => c.cardCount).join(',');
  const listRef = useRef<HTMLDivElement>(null);
  // scale shrinks the row to fit; dx nudges it horizontally (see effect below).
  // The STRIP seeds itself from the last fit it computed: switching tables
  // remounts the header it lives in, and starting at scale 1 paints one frame
  // at full size before the measurement shrinks it — the strip appearing to
  // zoom on every swipe. Its slot geometry is cap-derived, so the previous fit
  // is the correct answer for the same band, not merely a good guess.
  const [fit, setFit] = useState<{ scale: number; dx: number }>(() =>
    stripLayout ? lastStripFit() : { scale: 1, dx: 0 }
  );

  // Padding-aware overlap: pull each fan toward the previous one only by the
  // empty padding the two share, leaving a constant TARGET_GAP. Omaha fans
  // (little padding) barely overlap; Hold'em fans (more padding) tuck in tight.
  const marginLeftFor = (i: number): number | undefined => {
    if (i === 0) return undefined;
    // The strip uses ONE pitch for every slot (sized for the widest fan), so a
    // hand changing from 2 hole cards to 4 — or switching to a table whose
    // cards have just arrived — never nudges its neighbours.
    if (stripLayout) return stripCellMargin();
    // Bare rows solve for the TILE edges, so the visible gap is TILE_GAP
    // whatever mix of Hold'em and Omaha happens to be open. See gapMargin.
    return gapMargin(
      previewBoxWidth(cells[i - 1].cardCount, true),
      previewBoxWidth(cells[i].cardCount, true)
    );
  };

  // The LEADING "+" slot tucks against the FIRST fan the same padding-aware way.
  // It used to trail the row here (and lead only in the strip), so the control
  // jumped from the right edge to the left as the window narrowed — the same
  // button in two places depending on width. It now leads in both layouts, so
  // its position is a fact about the app rather than about the viewport
  // (design call, Jason 2026-08-16). Padding-aware against cells[0] because
  // that is the neighbour it now shares an edge with.
  // Same rule as between two tiles, so the "+" sits exactly as far from the
  // first tile as the tiles sit from each other.
  const addSlotMargin =
    cells.length > 0 ? gapMargin(ADD_SLOT_SPAN, previewBoxWidth(cells[0].cardCount, true)) : 0;

  // Trailing content starts TILE_GAP from the LAST TILE'S DRAWN EDGE, not from
  // its slot's edge — the slot is wider than the tile, and by an amount that
  // depends on that hand's card count.
  const trailingMargin =
    cells.length > 0
      ? TILE_GAP - (HAND_BOX - previewBoxWidth(cells[cells.length - 1].cardCount, true)) / 2
      : TILE_GAP;

  // In the strip the "+" LEADS the cluster, so it eats the padding it shares
  // with the FIRST card instead — leaving exactly the between-tables gap beside
  // it, so it reads as the next table going into the row. Fixed (widest-fan)
  // like every other slot, so the "+" doesn't shuffle when a hand changes size.
  const stripAddSlotMargin = cells.length > 0 ? stripAddMargin() : 0;

  // Fit-to-width + centered-until-it-can't:
  //   • Prefer the row TRUE page-centered.
  //   • If page-centering would push it under a side cluster, slide it left
  //     just enough to stay inside the safe band (the inset parent in
  //     TableHeader, whose box = the gap between the clusters).
  //   • Only if it still won't fit do we scale it down (floor keeps taps usable).
  useLayoutEffect(() => {
    const measure = () => {
      const el = listRef.current;
      const band = el?.parentElement;
      if (!el || !band) return;
      const brect = band.getBoundingClientRect();
      const safeL = brect.left;
      const safeR = brect.right;
      const bandWidth = Math.max(1, safeR - safeL);
      const bandCenter = (safeL + safeR) / 2; // where justify-center puts the row
      // Natural (unscaled) row width from box widths + overlap margins.
      // scrollWidth is unreliable with overflow:visible + negative margins.
      // Starts at zero when there are no fans at all — a row that is only the
      // trailing "+" measures one box, not two, or the fit math would shrink the
      // slot to fit a width it doesn't occupy.
      // The strip sizes itself for a FULL strip (the table cap), not for what
      // is open at this instant — so the scale is a constant for a given band,
      // and opening/closing/switching tables never resizes a card.
      let natural = stripLayout
        ? stripNaturalWidth(stripCapacity)
        : cells.length > 0
        ? HAND_BOX
        : 0;
      if (!stripLayout) {
        for (let i = 1; i < cells.length; i++) natural += HAND_BOX + (marginLeftFor(i) || 0);
        // The trailing "+" slot is part of the row, so it must be measured too —
        // otherwise the fit/center math drifts and the slot can run under a side
        // cluster.
        if (onAddTable) natural += HAND_BOX + addSlotMargin;
      }
      const scale = Math.min(1, Math.max(0.5, bandWidth / natural));
      const width = natural * scale;
      // Desired center = page center, clamped so the row stays in the band.
      const lo = safeL + width / 2;
      const hi = safeR - width / 2;
      const pageCenter = window.innerWidth / 2;
      const center = lo > hi ? bandCenter : Math.max(lo, Math.min(hi, pageCenter));
      const next = { scale, dx: center - bandCenter };
      // Remember it so the next mount (a table switch) paints at this size
      // instead of starting over at full scale. Only for the strip: the centred
      // fan rows genuinely differ per context and must keep measuring fresh.
      if (stripLayout && bandWidth > 1) rememberedStripFit = next;
      setFit((prev) => (prev.scale === next.scale && prev.dx === next.dx ? prev : next));
    };
    measure();
    // The BAND itself resizes independently of this row's content: its insets
    // are measured from the floating corner clusters, which mount and change
    // width after our first paint. Without observing it, the row keeps the
    // scale it computed against the initial (wider) band and overflows into
    // those clusters — the reported "cards overlap Sit Out" bug.
    const band = listRef.current?.parentElement;
    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => measure());
    if (observer && band) observer.observe(band);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
    // cellSig captures the count AND per-cell card counts, so the centred fan
    // row re-measures whenever its composition (fans + waitlist cards) changes.
    // The STRIP deliberately ignores composition — its scale comes from the cap
    // — so it only re-measures on a capacity/band change (and window resize).
  }, [cellSig, onAddTable, addSlotMargin, stripLayout, stripCapacity]);

  const { scale, dx } = fit;
  // Everything that can move the dot: which table is selected, what the row is
  // made of, and how it has been fitted to the band.
  const layoutSig = `${activeGameID ?? ''}|${cellSig}|${scale}|${dx}|${onAddTable ? 1 : 0}`;
  // No table is being viewed in the lobby strip — nothing to mark there.
  const hasSelection = !!activeGameID && !gridViewDots;

  // The in-table previews render a bit smaller than the lobby strip so the fans
  // sit more compactly on the felt / in the switcher. The strip's size is
  // ceilinged at that same dock size — see stripEffectiveScale.
  const effectiveScale = stripLayout ? stripEffectiveScale(scale) : scale * BARE_BASE_SCALE;
  // Skip the page-centering nudge when the parent owns centering (grouped
  // layout) — and in strip mode, where the row spans its band and pins its own
  // edges rather than floating toward page centre.
  const tx = autoCenter && !stripLayout ? dx : 0;
  const listStyle: CSSProperties = {
    gap: 0,
    transform: `translateX(${tx}px) scale(${effectiveScale})`,
    // Scale from the vertical centre so the shrink keeps the cards' centre put
    // (a top origin lifted them above the box centre, misaligning siblings).
    transformOrigin: 'center center'
  };

  // The strip centres its content as ONE group — the "+" first, then the card
  // cluster — so it stays balanced in the band at every table count. The group
  // widens with each table opened, carrying the "+" further toward the left
  // edge and the newest card toward the right, until at the table cap the cards
  // fill the strip on their own (the "+" is gone by then).
  if (stripLayout) {
    listStyle.justifyContent = 'center';
    listStyle.transformOrigin = 'center center';
    // The row spans the whole band, so it takes NO pointer input itself — only
    // the cards and the "+" do (re-enabled in the stylesheet). Left clickable,
    // it is an invisible full-width rectangle across the top of the screen that
    // swallows whatever sits beneath it: the landscape Exit Table
    // "Leave / Stay" confirm was unclickable for exactly this reason, and drags
    // starting up here never reached the felt.
    listStyle.pointerEvents = 'none';
  }

  const addButton = onAddTable ? (
    <button
      type='button'
      className={styles.addSlot}
      style={
        // Leads the row in BOTH layouts — tucked one between-tables gap from the
        // first card. The strip additionally floors the slot at the minimum tap
        // target, since that layout is the touch one.
        stripLayout
          ? {
              minWidth: TAP_TARGET_MIN,
              minHeight: TAP_TARGET_MIN,
              marginRight: cells.length > 0 ? stripAddSlotMargin : undefined
            }
          : { marginRight: cells.length > 0 ? addSlotMargin : undefined }
      }
      onClick={onAddTable}
      title='Add table'
      aria-label='Add table'>
      <span className={styles.addSlotInner} aria-hidden>
        +
      </span>
    </button>
  ) : null;

  // --- Navigation strip: centred group, "+" leading the card cluster ---------
  // One row, one slot per open table (capped by MAX_OPEN_TABLES; the layout
  // carries five so raising the cap needs no layout change). The "+" always
  // leads, the cards follow as one cluster, and a new table joins at the
  // cluster's right end — pushing the existing cards one slot LEFT. The cards
  // themselves are the same fans, in the same boxes, at the same size.
  if (stripLayout) {
    return (
      <div
        ref={listRef}
        className={`${styles.list} ${styles.listBare} ${styles.listStrip} ${
          animateIn ? styles.listAnimateIn : ''
        }`}
        style={listStyle}
        data-testid='table-nav-strip'>
        {addButton}
        {cells.length > 0 && (
          <div className={styles.stripCluster} data-testid='table-nav-strip-cluster'>
            {cells.map((c, i) => c.render(marginLeftFor(i)))}
          </div>
        )}
        {trailing && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              height: HAND_SLOT_HEIGHT,
              marginLeft: TILE_GAP
            }}>
            {trailing}
          </div>
        )}
        {/* Sibling of the cluster, not a child of it: the cluster's children are
            the slots, and both the layout math here and the band measurement in
            TableHeader walk them. The dot is absolutely positioned against the
            row either way. */}
        <SelectionDot enabled={hasSelection} layoutSig={layoutSig} />
      </div>
    );
  }

  return (
    <div
      ref={listRef}
      className={`${styles.list} ${styles.listBare} ${animateIn ? styles.listAnimateIn : ''}`}
      style={listStyle}>
      {/* "+" leads here too — same position at every width (see addSlotMargin). */}
      {addButton}
      {cells.map((c, i) => c.render(marginLeftFor(i)))}
      {trailing && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: HAND_SLOT_HEIGHT,
            marginLeft: trailingMargin
          }}>
          {trailing}
        </div>
      )}
      <SelectionDot enabled={hasSelection} layoutSig={layoutSig} />
    </div>
  );
};

const YourTables: React.FC<YourTablesProps> = ({
  tables,
  onOpenTable,
  bare = false,
  activeGameID,
  onAddTable,
  showWaitlist = true,
  animateIn,
  autoCenter,
  rootRef,
  className,
  stripLayout = false,
  stripCapacity,
  gridViewDots = false,
  trailing
}) => {
  // Tables the player is queued for live in redux; the strip surfaces them
  // next to the hands they're actually seated at.
  const mySpots = useAppSelector((state) => state.waitlist.mySpotByGameId);
  // Exclude only tables the player is actually SEATED at (sitting down dequeues
  // the waitlist, so a seated table shows its real hand). A table the player is
  // merely OBSERVING while queued still shows its waitlist card — otherwise it
  // would flip from the "WAITLIST" card to an observing hand once opened, which
  // looked inconsistent between first load and after visiting the table.
  const seatedIds = new Set(tables.filter((t) => t.isSeated).map((t) => Number(t.gameID)));
  const waitlistEntries = showWaitlist
    ? Object.values(mySpots).filter((e) => !seatedIds.has(e.gameId))
    : [];
  // The player's open tables. A queued one keeps its own slot and shows its
  // waitlist card there (see "ONE ORDER, EVERY ROW" below).
  const openTableIds = new Set(tables.map((t) => Number(t.gameID)));
  // A ready seat (my turn + an open seat) shows the JOIN countdown instead of
  // the WAIT stamp. It no longer changes where the card sits — see the ordering
  // note below.
  const isReady = (e: WaitlistEntry) => e.yourTurn && e.availableSeats > 0;
  const readyEntries = waitlistEntries.filter(isReady);
  const waitingEntries = waitlistEntries.filter((e) => !isReady(e));

  // Nothing to show: no hands and no waitlist cards. The exception is an add
  // slot — with "Stack Your Active Tables" off, the in-table spread excludes the
  // table you are already looking at (EN-10341), so a player with a single table
  // open hands us an EMPTY list while still needing the trailing "+" to open
  // another one. The row renders that on its own.
  // LOBBY STRIP FIT (owner, 2026-09-08). The panel never scrolls and never
  // wraps; it gives up its label in stages instead, and scales the fans only as
  // a last resort:
  //   inline  — "Current Tables" on one line, when the fans and that label fit;
  //   stacked — "Current" over "Tables" (the narrower column) when only that fits;
  //   hidden  — fans alone; and if even they do not fit, the row SCALES down so
  //             four Omaha fans still sit on one line at the 390px floor.
  // Measured against the panel's own inner width (ResizeObserver), with the two
  // label column widths read from the stylesheet's custom properties so the CSS
  // stays the single source of truth for what a label costs.
  const stripEl = useRef<HTMLDivElement | null>(null);
  const listEl = useRef<HTMLDivElement | null>(null);
  const [stripFit, setStripFit] = useState<{
    label: 'inline' | 'stacked' | 'hidden';
    scale: number;
  }>({ label: 'inline', scale: 1 });
  const setStripRef = useCallback(
    (el: HTMLDivElement | null) => {
      stripEl.current = el;
      if (typeof rootRef === 'function') rootRef(el);
      else if (rootRef) (rootRef as MutableRefObject<HTMLDivElement | null>).current = el;
    },
    [rootRef]
  );
  // Any change in composition re-fits — and the count has to be what the row
  // RENDERS, not what it was handed. A table the player is both observing and
  // queued for occupies ONE cell: the table keeps its slot and the entry renders
  // inside it. That is `tables + queued - overlap`.
  //
  // Summing the two without the overlap meant a change in the OVERLAP alone —
  // leaving one table's queue to join another's, say — left the total
  // unchanged and never re-ran this effect. Nothing else would catch it
  // either: the panel's fixed max-width means its own ResizeObserver stays
  // silent, so a stacked label or a scale that now had room to undo simply
  // stayed until the next viewport resize (review, 2026-09-13).
  const queuedAndOpen = waitlistEntries.filter((e) => openTableIds.has(Number(e.gameId))).length;
  const lobbyCellCount = tables.length + waitlistEntries.length - queuedAndOpen;
  useLayoutEffect(() => {
    if (bare) return undefined;
    const strip = stripEl.current;
    const list = listEl.current;
    if (!strip || !list) return undefined;
    const px = (v: string, fallback = 0) => {
      const n = parseFloat(v);
      return Number.isFinite(n) ? n : fallback;
    };
    const measure = () => {
      const cs = getComputedStyle(strip);
      const inner = strip.clientWidth - px(cs.paddingLeft) - px(cs.paddingRight);
      if (inner <= 0) return;
      const columnGap = px(cs.columnGap);
      const inlineLabel = px(cs.getPropertyValue('--strip-label-inline'), 146);
      const stackedLabel = px(cs.getPropertyValue('--strip-label-stacked'), 100);
      const n = list.children.length;
      const ls = getComputedStyle(list);
      const natural =
        n * HAND_BOX +
        Math.max(0, n - 1) * px(ls.columnGap) +
        px(ls.paddingLeft) +
        px(ls.paddingRight);
      let label: 'inline' | 'stacked' | 'hidden' = 'hidden';
      let scale = 1;
      if (natural + columnGap + inlineLabel <= inner) label = 'inline';
      else if (natural + columnGap + stackedLabel <= inner) label = 'stacked';
      else if (natural > inner) scale = Math.max(0.6, Math.floor((inner / natural) * 100) / 100);
      setStripFit((prev) =>
        prev.label === label && prev.scale === scale ? prev : { label, scale }
      );
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(strip);
    return () => observer?.disconnect();
  }, [bare, lobbyCellCount]);

  if (tables.length === 0 && waitlistEntries.length === 0 && !onAddTable) return null;

  // ONE ORDER, EVERY ROW (EN-10793).
  //
  // `tables` arrives in the shared table order (useActiveTables), which is the
  // same order MultiTableGrid lays its cells out in and the carousel swipes
  // through. Every row that shows the player's tables renders exactly that,
  // unsorted: the lobby strip, the in-table nav strip, the grid header's tiles
  // and the switcher dropdown all read left-to-right the same way, so a tile
  // always maps to the table in the matching grid cell.
  //
  // Rows used to lift a queued table out of the row and re-append it after the
  // hands — everywhere but the mobile strip — which put waitlist tables in grid
  // cells 1 and 4 while their tiles sat in nav positions 3 and 4. A queued
  // table now keeps its OWN slot on every row and renders its WAIT / JOIN card
  // there; a ready seat announces itself on that card's countdown rather than
  // by moving.
  //
  // Entries for tables that ARE open — rendered in that table's own slot, keyed
  // by gameId…
  const inlineWaitlist = waitlistEntries.reduce<Record<string, WaitlistEntry>>((acc, e) => {
    if (openTableIds.has(Number(e.gameId))) acc[String(e.gameId)] = e;
    return acc;
  }, {});
  // …and the leftovers: tables the player queued from the lobby without ever
  // opening. They have no slot in this row because they have no table in it —
  // and no grid cell either, so there is no position for them to match. They
  // bookend the row instead (ready seats lead, still-queued trail), identically
  // in the lobby strip and in the grid's nav strip.
  const orphanEntries = (list: WaitlistEntry[]) =>
    list.filter((e) => !openTableIds.has(Number(e.gameId)));

  // Bare: the card fans + inline waitlist cards (table overlay / switcher / grid).
  if (bare) {
    return (
      <BareFans
        tables={tables}
        onOpenTable={onOpenTable}
        activeGameID={activeGameID}
        onAddTable={onAddTable}
        readyEntries={orphanEntries(readyEntries)}
        waitingEntries={orphanEntries(waitingEntries)}
        inlineWaitlist={inlineWaitlist}
        animateIn={animateIn}
        autoCenter={autoCenter}
        stripLayout={stripLayout}
        stripCapacity={stripCapacity}
        gridViewDots={gridViewDots}
        trailing={trailing}
      />
    );
  }

  const stripLabelClass =
    stripFit.label === 'inline'
      ? styles.stripInline
      : stripFit.label === 'hidden'
      ? styles.stripHidden
      : '';
  return (
    <>
      {/* The scrim (see YourTables.module.scss .scrim): a zero-height sticky
          sibling pinned to the panel's own top that fades the tiles passing
          beside and beneath the pinned panel instead of cutting them off. It
          exists exactly when the panel does. */}
      <div className={styles.scrim} aria-hidden />
      <div
        className={[styles.strip, stripLabelClass, className ?? ''].filter(Boolean).join(' ')}
        ref={setStripRef}>
        {/* The panel's section heading (EN-10751). It is an <h2> at every width:
            when the panel is too narrow to print the words it keeps them for a
            screen reader instead of dropping out of the outline, so "Current
            Tables" is reachable from the rotor on a phone exactly as it is on a
            desktop. Replaces the role='group' the list used to carry in the
            hidden state — a heading is the mechanism this lobby navigates by. */}
        {stripFit.label !== 'hidden' ? (
          <h2 className={styles.label}>
            {/* The space matters now that this is a heading: without it the two
                spans are announced as one word, "CurrentTables". A whitespace-only
                run between flex items is not rendered, so the stacked layout is
                unchanged. */}
            <span>Current</span> <span>Tables</span>
          </h2>
        ) : (
          <h2 className={styles.labelHidden}>Current Tables</h2>
        )}
        <div
          className={`${styles.list}${stripFit.scale < 1 ? ` ${styles.listScaled}` : ''}`}
          ref={listEl}
          // Last resort only (see the fit effect). The measured factor is the
          // ONLY thing this effect writes; what it does to the row is CSS —
          // `.listScaled` scales the fans with `transform` and widens the box by
          // the inverse so the scaled result is exactly the panel's inner width.
          // This replaced `zoom`, which did shrink the layout box in one step
          // but is a legacy property whose hit-testing under nested transforms
          // is implementation-defined, and every fan here is a tap target that
          // opens a real-money table (review, 2026-09-16).
          style={
            stripFit.scale < 1
              ? ({ '--strip-scale': String(stripFit.scale) } as CSSProperties)
              : undefined
          }>
          {/* Seats ready on a table that ISN'T open lead the row — they have no
            slot of their own to sit in (see orphanEntries). */}
          {orphanEntries(readyEntries).map((e) => (
            <JoinReadyHand key={`wl-${e.gameId}`} entry={e} onOpenTable={onOpenTable} />
          ))}
          {/* The player's tables, in the shared table order — the same
            left-to-right sequence as the grid cells and the nav strip. A table
            the player is queued on (not seated) renders its waitlist card in its
            OWN slot here rather than being pulled out to the end. */}
          {tables.map((t) => {
            // Same rich preview as the in-table switcher: turn ring / countdown
            // / ACT, away-state, dashed outline (observing), trophy.
            const hand = <Hand key={t.id} table={t} onOpen={onOpenTable} showTurnIndicator />;
            const queued = inlineWaitlist[String(t.gameID)];
            if (!queued) return hand;
            const QueuedCard = queuedCardFor(queued);
            return (
              <QueuedCard
                key={t.id}
                entry={queued}
                onOpenTable={onOpenTable}
                // The open table's own guard and tile carry over to its card.
                disabled={Boolean(t.pendingShutdown && !t.isSeated)}
                whenGone={hand}
              />
            );
          })}
          {/* Still-queued entries for tables that aren't open trail the row. */}
          {orphanEntries(waitingEntries).map((e) => (
            <WaitingHand key={`wl-${e.gameId}`} entry={e} onOpenTable={onOpenTable} />
          ))}
        </div>
      </div>
    </>
  );
};

export default YourTables;
